(() => {
  const initFancybox = () => {
    if (!window.Fancybox) return;

    window.Fancybox.bind('[data-fancybox^="ProductGallery-"]', {
      animated: true,
      dragToClose: true,
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
      Thumbs: false,
    });
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.nazvaGalleryReady === 'true') return;

    const slider = gallery.querySelector('.nazva-product-gallery__slider');
    const scrollbar = gallery.querySelector('.nazva-product-gallery__scrollbar');
    const scrollbarThumb = gallery.querySelector('.nazva-product-gallery__scrollbar-thumb');

    if (!slider) return;

    gallery.dataset.nazvaGalleryReady = 'true';

    if (gallery.classList.contains('nazva-product-gallery--single')) return;

    const usesNativeTouchScroll = () =>
      window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches;

    const updateScrollbar = () => {
      if (!scrollbar || !scrollbarThumb) return;

      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
      const trackWidth = scrollbar.clientWidth;
      const thumbWidth = maxScrollLeft > 0 ? Math.max((slider.clientWidth / slider.scrollWidth) * trackWidth, 48) : trackWidth;
      const maxThumbLeft = trackWidth - thumbWidth;
      const progress = maxScrollLeft > 0 ? slider.scrollLeft / maxScrollLeft : 0;

      scrollbarThumb.style.width = `${thumbWidth}px`;
      scrollbarThumb.style.transform = `translateX(${maxThumbLeft * progress}px)`;
      scrollbar.setAttribute('aria-valuenow', Math.round(progress * 100));
      scrollbar.setAttribute('aria-disabled', maxScrollLeft > 0 ? 'false' : 'true');
    };

    let scrollFrame;
    slider.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateScrollbar);
    });

    const getMaxScrollLeft = () => Math.max(0, slider.scrollWidth - slider.clientWidth);

    const clampScrollLeft = (value) => Math.min(Math.max(value, 0), getMaxScrollLeft());

    const getSlideScrollPositions = () => {
      const items = Array.from(slider.querySelectorAll('.product__media-item'));
      const sliderLeft = slider.getBoundingClientRect().left;
      const sliderPaddingLeft = Number.parseFloat(window.getComputedStyle(slider).paddingLeft) || 0;
      const maxScrollLeft = getMaxScrollLeft();
      const positions = items.map((item) => {
        const itemLeft = item.getBoundingClientRect().left - sliderLeft + slider.scrollLeft - sliderPaddingLeft;

        return clampScrollLeft(itemLeft);
      });

      if (positions.length) {
        positions[0] = 0;
        positions[positions.length - 1] = maxScrollLeft;
      }

      return [...new Set(positions.map((position) => Math.round(position)))].sort((a, b) => a - b);
    };

    const getTargetSlideScrollLeft = (direction = 0) => {
      const positions = getSlideScrollPositions();
      const currentScrollLeft = slider.scrollLeft;

      if (!positions.length) return clampScrollLeft(currentScrollLeft);

      if (direction > 0) {
        return positions.find((position) => position > currentScrollLeft + 8) ?? positions[positions.length - 1];
      }

      if (direction < 0) {
        return positions
          .slice()
          .reverse()
          .find((position) => position < currentScrollLeft - 8) ?? positions[0];
      }

      let nearest = positions[0];
      let nearestDistance = Number.POSITIVE_INFINITY;

      positions.forEach((position) => {
        const distance = Math.abs(currentScrollLeft - position);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = position;
        }
      });

      return clampScrollLeft(nearest);
    };

    const snapToNearestSlide = (behavior = 'smooth', direction = 0) => {
      if (getMaxScrollLeft() <= 2) {
        updateScrollbar();
        return;
      }

      slider.scrollTo({
        left: getTargetSlideScrollLeft(direction),
        behavior,
      });
      updateScrollbar();
    };

    if (!usesNativeTouchScroll()) {
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let previousX = 0;
      let previousTime = 0;
      let velocity = 0;
      let momentumFrame;
      let hasMoved = false;
      let suppressNextClick = false;
      let suppressClickTimer;
      const dragThreshold = 8;

      const stopMomentum = () => {
        window.cancelAnimationFrame(momentumFrame);
        momentumFrame = undefined;
      };

      const runMomentum = () => {
        if (Math.abs(velocity) < 0.08) {
          snapToNearestSlide('smooth', velocity < 0 ? 1 : -1);
          return;
        }

        const previousScrollLeft = slider.scrollLeft;
        slider.scrollLeft = clampScrollLeft(slider.scrollLeft - velocity * 24);
        velocity *= 0.965;

        if (slider.scrollLeft === previousScrollLeft) {
          snapToNearestSlide('smooth', velocity < 0 ? 1 : -1);
          return;
        }

        momentumFrame = window.requestAnimationFrame(runMomentum);
      };

      slider.addEventListener('pointerdown', (event) => {
        const interactiveTarget = event.target.closest('a, button');

        if (interactiveTarget && !interactiveTarget.classList.contains('nazva-product-gallery__zoom')) return;

        stopMomentum();
        isDragging = true;
        hasMoved = false;
        startX = event.clientX;
        startScrollLeft = slider.scrollLeft;
        previousX = event.clientX;
        previousTime = performance.now();
        velocity = 0;
      });

      slider.addEventListener('pointermove', (event) => {
        if (!isDragging) return;

        const now = performance.now();
        const moved = event.clientX - startX;
        const deltaX = event.clientX - previousX;
        const deltaTime = now - previousTime || 16;

        if (Math.abs(moved) > dragThreshold) {
          hasMoved = true;
        }

        if (!hasMoved) return;

        event.preventDefault();
        if (!slider.hasPointerCapture(event.pointerId)) {
          slider.setPointerCapture(event.pointerId);
        }
        slider.classList.add('is-dragging');
        slider.scrollLeft = clampScrollLeft(startScrollLeft - moved);
        velocity = deltaX / deltaTime;
        previousX = event.clientX;
        previousTime = now;
      });

      const stopDragging = (event) => {
        if (!isDragging) return;
        isDragging = false;
        slider.classList.remove('is-dragging');
        if (slider.hasPointerCapture(event.pointerId)) {
          slider.releasePointerCapture(event.pointerId);
        }

        if (!hasMoved) {
          velocity = 0;
          updateScrollbar();
          return;
        }

        suppressNextClick = true;
        window.clearTimeout(suppressClickTimer);
        suppressClickTimer = window.setTimeout(() => {
          suppressNextClick = false;
        }, 350);
        const swipeDirection = slider.scrollLeft > startScrollLeft ? 1 : -1;

        if (Math.abs(slider.scrollLeft - startScrollLeft) < slider.clientWidth * 0.08) {
          snapToNearestSlide();
          return;
        }

        if (Math.abs(velocity) < 0.08) {
          snapToNearestSlide('smooth', swipeDirection);
          return;
        }

        runMomentum();
      };

      slider.addEventListener('pointerup', stopDragging);
      slider.addEventListener('pointercancel', stopDragging);
      slider.addEventListener('pointerleave', stopDragging);

      slider.addEventListener(
        'click',
        (event) => {
          if (!suppressNextClick) return;
          event.preventDefault();
          event.stopPropagation();
          suppressNextClick = false;
          window.clearTimeout(suppressClickTimer);
        },
        true
      );

      slider.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

          event.preventDefault();
          stopMomentum();
          slider.scrollLeft = clampScrollLeft(slider.scrollLeft + event.deltaY);
        },
        { passive: false }
      );
    }

    if (scrollbar && scrollbarThumb) {
      let isScrollbarDragging = false;
      let scrollbarStartX = 0;
      let scrollbarStartScrollLeft = 0;

      scrollbar.addEventListener('click', (event) => {
        if (event.target === scrollbarThumb) return;

        const rect = scrollbar.getBoundingClientRect();
        const progress = (event.clientX - rect.left) / rect.width;
        slider.scrollTo({
          left: (slider.scrollWidth - slider.clientWidth) * progress,
          behavior: 'smooth',
        });
      });

      scrollbar.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        const step = slider.clientWidth * 0.25;
        const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

        if (event.key === 'Home') {
          slider.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (event.key === 'End') {
          slider.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
        } else {
          slider.scrollBy({ left: event.key === 'ArrowRight' ? step : -step, behavior: 'smooth' });
        }
      });

      scrollbarThumb.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        isScrollbarDragging = true;
        scrollbarStartX = event.clientX;
        scrollbarStartScrollLeft = slider.scrollLeft;
        scrollbarThumb.setPointerCapture(event.pointerId);
      });

      scrollbarThumb.addEventListener('pointermove', (event) => {
        if (!isScrollbarDragging) return;

        const trackWidth = scrollbar.clientWidth - scrollbarThumb.offsetWidth;
        const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
        const dragProgress = trackWidth > 0 ? (event.clientX - scrollbarStartX) / trackWidth : 0;
        slider.scrollLeft = scrollbarStartScrollLeft + maxScrollLeft * dragProgress;
      });

      const stopScrollbarDragging = (event) => {
        if (!isScrollbarDragging) return;
        isScrollbarDragging = false;
        if (scrollbarThumb.hasPointerCapture(event.pointerId)) {
          scrollbarThumb.releasePointerCapture(event.pointerId);
        }
      };

      scrollbarThumb.addEventListener('pointerup', stopScrollbarDragging);
      scrollbarThumb.addEventListener('pointercancel', stopScrollbarDragging);
    }

    window.addEventListener('resize', updateScrollbar);
    if (usesNativeTouchScroll()) {
      window.requestAnimationFrame(() => {
        slider.scrollLeft = 0;
        updateScrollbar();
      });
    }
    updateScrollbar();
  };

  const init = () => {
    initFancybox();
    document.querySelectorAll('media-gallery.nazva-product-gallery').forEach(initGallery);
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  // Nazva Gallery Toggle
  const initGalleryToggle = () => {
    const containers = document.querySelectorAll('.nazva-media-galleries-container');
    
    containers.forEach(container => {
      if (container.dataset.nazvaToggleReady === 'true') return;
      
      const toggle = container.querySelector('.nazva-gallery-toggle');
      if (!toggle) return;
      
      const primaryWrapper = container.querySelector('.nazva-media-gallery-wrapper--primary');
      const secondaryWrapper = container.querySelector('.nazva-media-gallery-wrapper--secondary');
      const buttons = toggle.querySelectorAll('.nazva-gallery-toggle__btn');
      
      if (!primaryWrapper || !secondaryWrapper) return;
      
      if (container.classList.contains('is-secondary-default')) {
        toggle.classList.add('is-secondary-active');
      }
      
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const target = btn.dataset.target;
          
          buttons.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          
          if (target === 'secondary') {
            toggle.classList.add('is-secondary-active');
            primaryWrapper.classList.add('hidden');
            secondaryWrapper.classList.remove('hidden');
          } else {
            toggle.classList.remove('is-secondary-active');
            secondaryWrapper.classList.add('hidden');
            primaryWrapper.classList.remove('hidden');
          }
        });
      });
      
      container.dataset.nazvaToggleReady = 'true';
    });
  };

  document.addEventListener('DOMContentLoaded', initGalleryToggle);
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initGalleryToggle();
  }
})();
